import { useCallback, useEffect, useState } from "react";
import { Box, CssBaseline, ThemeProvider } from "@mui/material";
import { v2Theme } from "../theme/v2Theme";
import "../theme/v2-styles.css";
import TopNav from "./TopNav";
import SideNav from "./SideNav";
import { useParentSession } from "../session";

const MOBILE_NAV_MQ = "(max-width: 899px)";

const AppShell = ({ children, activeNavId }) => {
  const [navOpen, setNavOpen] = useState(false);
  const [navCollapsed, setNavCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const details = useParentSession();

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia(MOBILE_NAV_MQ);
    const sync = () => {
      setIsMobile(mq.matches);
      if (!mq.matches) setNavOpen(false);
    };
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    if (!navOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [navOpen]);

  const closeNav = useCallback(() => setNavOpen(false), []);

  const onNavToggle = useCallback(() => {
    if (isMobile) {
      setNavOpen((open) => !open);
    } else {
      setNavCollapsed((collapsed) => !collapsed);
    }
  }, [isMobile]);

  const navExpanded = isMobile ? navOpen : !navCollapsed;

  return (
    <ThemeProvider theme={v2Theme}>
      <CssBaseline />
      <Box
        id="edsimsV2-root"
        className={[
          "edsimsV2-app-shell",
          navOpen ? "edsimsV2-app-shell--nav-open" : "",
          navCollapsed ? "edsimsV2-app-shell--nav-collapsed" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <a href="#edsimsV2-main" className="edsimsV2-skip-link">
          Skip to main content
        </a>
        <SideNav
          activeId={activeNavId}
          accType={details.accType}
          roleName={details.roleName}
          userName={details.fullName}
          avatarUrl={details.avatarUrl}
          userId={details.userId}
          open={navOpen}
          onClose={closeNav}
          collapsed={navCollapsed}
          onNavToggle={onNavToggle}
        />

        <Box className="edsimsV2-app-shell-content">
          <TopNav
            greetingName={details.firstName}
            fullName={details.fullName}
            avatarUrl={details.avatarUrl}
            userId={details.userId}
            navExpanded={navExpanded}
            onNavToggle={onNavToggle}
          />
          <Box
            component="main"
            id="edsimsV2-main"
            className="edsimsV2-app-shell-main"
            tabIndex={-1}
          >
            {children}
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default AppShell;
